import React, { useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import {
  FUEL_COLORS,
  REGION_COLORS,
  REGION_LABELS,
  generateAssetColor,
  formatDateTime,
  formatMWh,
  useContainerWidth,
  ChartSection,
  CHART_MARGINS,
  defaultLegendProps,
  rotatedTickLabelStyle,
  tickLabelStyle,
} from './shared';

const VIEW_MODES = [
  { value: 'asset', label: 'Asset' },
  { value: 'fuel', label: 'Fuel Mix' },
  { value: 'region', label: 'Region' },
];

/**
 * Stacked bar chart of generation (MWh) per time bucket.
 * Supports three views: by Asset, by Fuel Mix, or by Region.
 */
const GenerationBarChart = ({ generation, assets, frequency }) => {
  const { ref, width } = useContainerWidth();
  const [viewMode, setViewMode] = useState('asset');

  if (!generation || generation.length === 0) {
    return (
      <ChartSection title="Generation" subtitle="Total energy generated per period (MWh)">
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">No generation data available for this period</Typography>
        </Box>
      </ChartSection>
    );
  }

  const assetInfo = {};
  (assets || []).forEach((a, i) => {
    assetInfo[a.asset_id] = { ...a, colorIndex: i };
  });

  const { xLabels, series } = buildSeries(viewMode, generation, assetInfo, frequency);

  const viewToggle = (
    <ToggleButtonGroup
      value={viewMode}
      exclusive
      onChange={(_, v) => { if (v) setViewMode(v); }}
      size="small"
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          px: 1.5,
          py: 0.4,
          borderColor: 'rgba(0,0,0,0.12)',
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: '#fff',
            '&:hover': { bgcolor: 'primary.dark' },
          },
        },
      }}
    >
      {VIEW_MODES.map((m) => (
        <ToggleButton key={m.value} value={m.value}>{m.label}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  return (
    <ChartSection
      title="Generation"
      subtitle="Total energy generated per period (MWh)"
      headerRight={viewToggle}
    >
      <Box ref={ref} sx={{ width: '100%', height: 420, minWidth: 0, overflow: 'hidden' }}>
        {width > 0 && (
          <BarChart
            width={width}
            height={420}
            series={series}
            xAxis={[{
              scaleType: 'band',
              data: xLabels,
              tickLabelStyle: rotatedTickLabelStyle,
              tickNumber: xLabels.length > 30 ? 30 : undefined,
            }]}
            yAxis={[{ valueFormatter: formatMWh, tickLabelStyle }]}
            margin={CHART_MARGINS}
            slotProps={{ legend: defaultLegendProps }}
          />
        )}
      </Box>
    </ChartSection>
  );
};

// ── Series builders per view mode ──────────────────────────────────────────

function buildSeries(viewMode, generation, assetInfo, frequency) {
  switch (viewMode) {
    case 'fuel':
      return buildFuelMixSeries(generation, assetInfo, frequency);
    case 'region':
      return buildRegionSeries(generation, assetInfo, frequency);
    default:
      return buildAssetSeries(generation, assetInfo, frequency);
  }
}

/** Asset view — one series per asset, colored by fuel type or index */
function buildAssetSeries(generation, assetInfo, frequency) {
  const buckets = {};
  const assetIds = new Set();
  generation.forEach(({ date_time, asset_id, value_mwh }) => {
    const key = String(date_time);
    if (!buckets[key]) buckets[key] = {};
    buckets[key][asset_id] = (buckets[key][asset_id] || 0) + value_mwh;
    assetIds.add(asset_id);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const sortedAssets = Array.from(assetIds).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const assetColor = (assetId, idx) => {
    const info = assetInfo[assetId];
    if (info?.fuel_code && FUEL_COLORS[info.fuel_code]) {
      return FUEL_COLORS[info.fuel_code];
    }
    return generateAssetColor(idx);
  };

  const series = sortedAssets.map((aid, idx) => ({
    label: assetInfo[aid]?.name || aid,
    data: sortedBuckets.map((b) => buckets[b][aid] || 0),
    stack: 'gen',
    color: assetColor(aid, idx),
  }));

  return { xLabels, series };
}

/** Fuel Mix view — one series per fuel type, aggregated across assets */
function buildFuelMixSeries(generation, assetInfo, frequency) {
  const buckets = {};
  const fuelCodes = new Set();
  generation.forEach(({ date_time, asset_id, value_mwh }) => {
    const key = String(date_time);
    const fuel = assetInfo[asset_id]?.fuel_code || 'Other';
    if (!buckets[key]) buckets[key] = {};
    buckets[key][fuel] = (buckets[key][fuel] || 0) + value_mwh;
    fuelCodes.add(fuel);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const sortedFuels = [...fuelCodes].sort((a, b) => {
    const totalA = sortedBuckets.reduce((sum, k) => sum + (buckets[k][a] || 0), 0);
    const totalB = sortedBuckets.reduce((sum, k) => sum + (buckets[k][b] || 0), 0);
    return totalB - totalA;
  });

  const series = sortedFuels.map((fuel) => ({
    label: fuel,
    data: sortedBuckets.map((b) => buckets[b][fuel] || 0),
    stack: 'fuel',
    color: FUEL_COLORS[fuel] || '#6b7280',
  }));

  return { xLabels, series };
}

/** Region view — one series per NEM region, aggregated across assets */
function buildRegionSeries(generation, assetInfo, frequency) {
  const buckets = {};
  const regions = new Set();
  generation.forEach(({ date_time, asset_id, value_mwh }) => {
    const key = String(date_time);
    const region = assetInfo[asset_id]?.region_id || 'Unknown';
    if (!buckets[key]) buckets[key] = {};
    buckets[key][region] = (buckets[key][region] || 0) + value_mwh;
    regions.add(region);
  });

  const sortedBuckets = Object.keys(buckets).sort();
  const xLabels = sortedBuckets.map((b) => formatDateTime(b, frequency));

  const sortedRegions = [...regions].sort((a, b) => {
    const totalA = sortedBuckets.reduce((sum, k) => sum + (buckets[k][a] || 0), 0);
    const totalB = sortedBuckets.reduce((sum, k) => sum + (buckets[k][b] || 0), 0);
    return totalB - totalA;
  });

  const series = sortedRegions.map((region) => ({
    label: REGION_LABELS[region] || region,
    data: sortedBuckets.map((b) => buckets[b][region] || 0),
    stack: 'region',
    color: REGION_COLORS[region] || '#6b7280',
  }));

  return { xLabels, series };
}

export default GenerationBarChart;
